import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

interface User {
  id: string;
  email: string;
  name: string;
  tokenBalance: number;
  tokens: number;
  isEmailVerified: boolean;
  subscription: {
    status: string;
    plan: string | null;
  };
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string) => Promise<string>;
  logout: () => void;
  updateUser: (userData: Partial<User>) => Promise<void>;
  resendVerificationEmail: (email: string) => Promise<string>;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  setUser: (user: User | null) => void;
  setIsAuthenticated: (isAuthenticated: boolean) => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Configure axios defaults and verify token on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // Verify token immediately
      axios.get(`${import.meta.env.VITE_API_URL}/api/auth/verify`)
        .then(response => {
          const userData = response.data.user;
          setUser({
            ...userData,
            tokenBalance: userData.tokenBalance || 0
          });
          setIsAuthenticated(true);
        })
        .catch(() => {
          // If verification fails, clear everything
          localStorage.removeItem('token');
          delete axios.defaults.headers.common['Authorization'];
          setUser(null);
          setIsAuthenticated(false);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setIsLoading(false);
    }
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await axios.post(`${import.meta.env.VITE_API_URL}/api/auth/login`, { email, password });
      
      if (response.data.requiresVerification) {
        localStorage.setItem('pendingVerificationEmail', email);
        throw new Error(response.data.message);
      }

      const { token, user } = response.data;
      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(user);
      setIsAuthenticated(true);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Login failed');
      }
      throw error;
    }
  };

  const register = async (email: string, password: string, name: string) => {
    try {
      const response = await axios.post(`${import.meta.env.VITE_API_URL}/api/auth/register`, {
        email,
        password,
        name
      });
      
      // Store email for verification resend functionality
      localStorage.setItem('pendingVerificationEmail', email);
      
      setError(null);

      // Return the message to display to the user
      return response.data.message;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        // Handle validation errors from express-validator
        if (error.response.data.errors && Array.isArray(error.response.data.errors)) {
          const errorMessages = error.response.data.errors.map((err: any) => err.msg).join(', ');
          setError(errorMessages);
          throw new Error(errorMessages);
        }
        
        // Handle other error messages
        const errorMessage = error.response.data.message || 'Registration failed';
        setError(errorMessage);
        throw new Error(errorMessage);
      }
      
      // Handle network errors or other issues
      const errorMessage = 'Registration failed. Please check your connection and try again.';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const resendVerificationEmail = async (email: string) => {
    try {
      const response = await axios.post(`${import.meta.env.VITE_API_URL}/api/email/resend`, { email });
      return response.data.message;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to resend verification email');
      }
      throw new Error('Failed to resend verification email');
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('pendingVerificationEmail');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setIsAuthenticated(false);
    setError(null);
  };

  const updateUser = async (userData: Partial<User>) => {
    try {
      const response = await axios.put(`${import.meta.env.VITE_API_URL}/api/auth/update`, userData);
      setUser(response.data);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to update user');
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        register,
        logout,
        updateUser,
        resendVerificationEmail,
        isAuthenticated,
        isLoading,
        error,
        setUser,
        setIsAuthenticated
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};